import React, { useCallback, useEffect, useRef, useState } from 'react'
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline'
import clsx from 'clsx'
import Card from '../ui/Card'
import Badge from '../ui/Badge'
import Button from '../ui/Button'
import CatalogAdminPanel from './CatalogAdminPanel'
import PassSequenceImagesAdmin from './PassSequenceImagesAdmin'
import PassNameMappingsAdmin from './PassNameMappingsAdmin'
import BevelGeometryImagesAdmin from './BevelGeometryImagesAdmin'
import { useUnitSetting } from '../../utils/userUnitSetting'
import { getJwtToken } from '../../config/cognito'
import { decodeJwtPayload, parseCognitoGroupsFromPayload } from '../../utils/jwtPayload'
import { AIConfigurationPanel } from '../ask-evans/AIConfigurationPanel'
import { AIUsagePanel } from '../ask-evans/AIUsagePanel'

type SettingsTab =
  | 'general'
  | 'standards'
  | 'access'
  | 'catalog'
  | 'passSequence'
  | 'passNameMappings'
  | 'bevelGeometry'
  | 'aiConfiguration'
  | 'aiUsage'

function SettingsTabBar({
  tabs,
  tab,
  onSelect
}: {
  tabs: Array<{ id: SettingsTab; label: string }>
  tab: SettingsTab
  onSelect: (id: SettingsTab) => void
}) {
  const scrollerRef = useRef<HTMLDivElement>(null)
  const activeRef = useRef<HTMLButtonElement>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  const refresh = useCallback(() => {
    const el = scrollerRef.current
    if (!el) return
    const maxScroll = el.scrollWidth - el.clientWidth
    setCanScrollLeft(el.scrollLeft > 1)
    setCanScrollRight(maxScroll - el.scrollLeft > 1)
  }, [])

  useEffect(() => {
    const el = scrollerRef.current
    if (!el) return
    refresh()
    el.addEventListener('scroll', refresh, { passive: true })
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(refresh) : null
    observer?.observe(el)
    window.addEventListener('resize', refresh)
    return () => {
      el.removeEventListener('scroll', refresh)
      observer?.disconnect()
      window.removeEventListener('resize', refresh)
    }
  }, [refresh, tabs])

  useEffect(() => {
    const node = activeRef.current
    if (node && typeof node.scrollIntoView === 'function') {
      node.scrollIntoView({ inline: 'nearest', block: 'nearest' })
    }
    refresh()
  }, [tab, refresh])

  function scrollByPage(direction: -1 | 1) {
    const el = scrollerRef.current
    if (!el) return
    const delta = Math.max(160, el.clientWidth * 0.7) * direction
    if (typeof el.scrollBy === 'function') {
      el.scrollBy({ left: delta, behavior: 'smooth' })
      return
    }
    el.scrollLeft += delta
  }

  const arrowClass = (enabled: boolean) =>
    clsx(
      'flex h-9 w-8 shrink-0 items-center justify-center rounded-md border bg-white shadow-sm',
      enabled
        ? 'border-blueprint-blue text-blueprint-blue hover:bg-page-bg'
        : 'cursor-default border-[#E2E5EA] text-heritage-grey opacity-40'
    )

  return (
    <div className="flex w-full min-w-0 items-center gap-1 border-b border-gray-200 pb-2">
      <button
        type="button"
        aria-label="Show tabs on the left"
        disabled={!canScrollLeft}
        onClick={() => scrollByPage(-1)}
        className={arrowClass(canScrollLeft)}
      >
        <ChevronLeftIcon className="h-4 w-4" aria-hidden />
      </button>
      <div
        ref={scrollerRef}
        className="min-w-0 flex-1 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        <div className="flex w-max flex-nowrap gap-1">
          {tabs.map((item) => (
            <button
              key={item.id}
              ref={tab === item.id ? activeRef : undefined}
              type="button"
              onClick={() => onSelect(item.id)}
              className={clsx(
                'shrink-0 whitespace-nowrap rounded-md px-2.5 py-2 text-sm font-medium transition',
                tab === item.id ? 'bg-primary-600 text-white' : 'text-gray-600 hover:bg-gray-100'
              )}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>
      <button
        type="button"
        aria-label="Show tabs on the right"
        disabled={!canScrollRight}
        onClick={() => scrollByPage(1)}
        className={arrowClass(canScrollRight)}
      >
        <ChevronRightIcon className="h-4 w-4" aria-hidden />
      </button>
    </div>
  )
}

export default function Settings() {
  const [tab, setTab] = useState<SettingsTab>('general')
  const [unit, setUnit] = useUnitSetting()
  const [isSuperAdmin, setIsSuperAdmin] = useState(false)

  useEffect(() => {
    let cancelled = false
    Promise.resolve(getJwtToken?.() ?? null)
      .then((token) => {
        if (cancelled) return
        const groups = parseCognitoGroupsFromPayload(token ? decodeJwtPayload(token) : null)
        setIsSuperAdmin(groups.includes('super_admin'))
      })
      .catch(() => {
        if (!cancelled) setIsSuperAdmin(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const tabs = [
    { id: 'general' as const, label: 'General' },
    { id: 'standards' as const, label: 'Standards & rules' },
    { id: 'access' as const, label: 'Access control' },
    { id: 'catalog' as const, label: 'Dropdown Catalog' },
    { id: 'passSequence' as const, label: 'Pass Sequence Images' },
    { id: 'passNameMappings' as const, label: 'Pass Name Mappings' },
    { id: 'bevelGeometry' as const, label: 'Bevel Geometry Images' },
    ...(isSuperAdmin
      ? [
          { id: 'aiConfiguration' as const, label: 'AI Configuration' },
          { id: 'aiUsage' as const, label: 'AI Usage' }
        ]
      : [])
  ]

  return (
    <div className="space-y-6">
      <SettingsTabBar tabs={tabs} tab={tab} onSelect={setTab} />

      {tab === 'general' && (
        <Card title="Units">
          <div data-testid="settings-units-card" className="space-y-3">
            <p className="text-xs text-gray-500">
              Affects telemetry pages (Pass Analytics). Existing WPS forms keep their per-field
              unit pickers.
            </p>
            <div className="space-y-2">
              <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
                <input
                  type="radio"
                  name="settings-units"
                  value="Imperial"
                  checked={unit === 'Imperial'}
                  onChange={() => setUnit('Imperial')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500"
                />
                Imperial (in, ipm, mil, kJ/in)
              </label>
              <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
                <input
                  type="radio"
                  name="settings-units"
                  value="Metric"
                  checked={unit === 'Metric'}
                  onChange={() => setUnit('Metric')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500"
                />
                Metric (mm, mm/s, mm, kJ/mm)
              </label>
            </div>
          </div>
        </Card>
      )}

      {tab === 'standards' && (
        <Card title="Standards & Validation Rules" action={<Badge tone="primary">ASME / ISO / DNV</Badge>}>
          <div className="grid gap-4 lg:grid-cols-2">
            <div>
              <p className="text-sm font-semibold text-gray-900">Active Code Set</p>
              <p className="text-xs text-gray-500">ASME IX + API 1104 (Pipeline)</p>
              <div className="mt-3 space-y-2 text-xs text-gray-500">
                <p>Heat input limits enforced per material group.</p>
                <p>Joint design checks against pipe size categories.</p>
                <p>Auto-flag shielding gas mismatches.</p>
              </div>
            </div>
            <div className="rounded-lg border border-gray-200 p-4">
              <p className="text-xs text-gray-500">Rule packs</p>
              <div className="mt-2 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">API 1104 Pipeline</span>
                  <Badge tone="success">Enabled</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">ASME IX Section IX</span>
                  <Badge tone="success">Enabled</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">ISO 15614</span>
                  <Badge tone="gray">Off</Badge>
                </div>
              </div>
            </div>
          </div>
        </Card>
      )}

      {tab === 'access' && (
        <Card title="Access Control">
          <div className="grid gap-4 lg:grid-cols-3">
            <div className="rounded-lg border border-gray-200 p-4">
              <p className="text-sm font-semibold text-gray-900">Engineer</p>
              <p className="text-xs text-gray-500">Create, clone, and edit WPS drafts.</p>
            </div>
            <div className="rounded-lg border border-gray-200 p-4">
              <p className="text-sm font-semibold text-gray-900">Reviewer</p>
              <p className="text-xs text-gray-500">Approve, comment, and sign revisions.</p>
            </div>
            <div className="rounded-lg border border-gray-200 p-4">
              <p className="text-sm font-semibold text-gray-900">QA Admin</p>
              <p className="text-xs text-gray-500">Manage standards, numbering, and templates.</p>
            </div>
          </div>
          <Button variant="secondary" className="mt-4">
            Configure Roles
          </Button>
        </Card>
      )}

      {tab === 'catalog' && (
        <Card
        >
          <CatalogAdminPanel />
        </Card>
      )}

      {tab === 'passSequence' && (
        <Card>
          <PassSequenceImagesAdmin />
        </Card>
      )}

      {tab === 'passNameMappings' && <PassNameMappingsAdmin />}

      {tab === 'bevelGeometry' && (
        <Card>
          <BevelGeometryImagesAdmin />
        </Card>
      )}

      {tab === 'aiConfiguration' && isSuperAdmin && <AIConfigurationPanel />}
      {tab === 'aiUsage' && isSuperAdmin && <AIUsagePanel />}
    </div>
  )
}
